import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Tag, Button, message, Spin, Modal, Table, Badge, Radio, Alert, Space, Pagination, Input, Select } from 'antd';
import {
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  EyeOutlined,
  CloseOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  TeamOutlined,
  RobotOutlined,
  CameraOutlined,
  VideoCameraTwoTone,
  SaveOutlined,
  PauseCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';


import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Search } = Input;
const { Option } = Select;

const describeIceCandidate = (candidate) => {
  const text = candidate?.candidate || '';
  return {
    type: text.match(/\styp\s([a-z]+)/i)?.[1] || '',
    protocol: text.match(/\s(udp|tcp)\s/i)?.[1]?.toLowerCase() || '',
    address: text.match(/candidate:\S+\s+\d+\s+\S+\s+\d+\s+([^\s]+)/i)?.[1] || '',
    port: text.match(/candidate:\S+\s+\d+\s+\S+\s+\d+\s+[^\s]+\s+(\d+)/i)?.[1] || ''
  };
};

const toPlainIceCandidate = (candidate) => {
  if (!candidate) return candidate;
  if (typeof candidate.toJSON === 'function') {
    return candidate.toJSON();
  }
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment
  };
};

const WEBRTC_MAX_RECONNECT_ATTEMPTS = 2;

const VIDEO_RECORDING_QUALITY_OPTIONS = {
  smooth: {
    key: 'smooth',
    label: '流畅',
    width: 1280,
    height: 720,
    frameRate: 15,
    senderBitrate: 1800000,
    recorderBitrate: 2500000
  },
  high: {
    key: 'high',
    label: '高清',
    width: 1920,
    height: 1080,
    frameRate: 15,
    senderBitrate: 4000000,
    recorderBitrate: 5000000
  },
  ultra: {
    key: 'ultra',
    label: '超清',
    width: 1920,
    height: 1080,
    frameRate: 20,
    senderBitrate: 6500000,
    recorderBitrate: 8000000
  }
};

const summarizeRtcStats = async (pc) => {
  if (!pc || typeof pc.getStats !== 'function') return null;

  try {
    const stats = await pc.getStats();
    const localCandidates = [];
    const remoteCandidates = [];
    const pairs = [];

    stats.forEach((item) => {
      if (item.type === 'local-candidate') {
        localCandidates.push({
          id: item.id,
          candidateType: item.candidateType,
          protocol: item.protocol,
          address: item.address || item.ip,
          port: item.port,
          networkType: item.networkType
        });
      } else if (item.type === 'remote-candidate') {
        remoteCandidates.push({
          id: item.id,
          candidateType: item.candidateType,
          protocol: item.protocol,
          address: item.address || item.ip,
          port: item.port
        });
      } else if (item.type === 'candidate-pair') {
        pairs.push({
          id: item.id,
          state: item.state,
          nominated: item.nominated,
          selected: item.selected,
          localCandidateId: item.localCandidateId,
          remoteCandidateId: item.remoteCandidateId,
          currentRoundTripTime: item.currentRoundTripTime
        });
      }
    });

    return {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState,
      localCandidateTypes: [...new Set(localCandidates.map(candidate => candidate.candidateType).filter(Boolean))],
      remoteCandidateTypes: [...new Set(remoteCandidates.map(candidate => candidate.candidateType).filter(Boolean))],
      localCandidates: localCandidates.slice(0, 12),
      remoteCandidates: remoteCandidates.slice(0, 12),
      pairs: pairs.slice(0, 12)
    };
  } catch (error) {
    return { error: error?.message || String(error) };
  }
};


/**
 * 视频考勤子页面
 * 支持两种考勤方式：按人数考勤、AI人脸识别考勤
 */
const VideoAttendance = () => {
  const { isSuperAdmin, user } = useAuth();
  const [membershipCaptureAllowed, setMembershipCaptureAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewingClass, setViewingClass] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cameraStatus, setCameraStatus] = useState({});
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraDeviceId, setSelectedCameraDeviceId] = useState(undefined);
  const [cameraDevicesLoading, setCameraDevicesLoading] = useState(false);
  const [cameraSwitching, setCameraSwitching] = useState(false);

  // 搜索和筛选状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [classTotal, setClassTotal] = useState(0);

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12); // 每页显示12个摄像头

  // 考勤方式选择
  const [attendanceMethod, setAttendanceMethod] = useState('count'); // 'count' | 'face'

  // 考勤相关状态
  const [isAttendanceMode, setIsAttendanceMode] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [attendanceResults, setAttendanceResults] = useState([]);
  const [detectedFaceCount, setDetectedFaceCount] = useState(0);
  const [seatLayout, setSeatLayout] = useState(null); // 座位布局信息

  // 拍照和录像相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingQuality, setRecordingQuality] = useState('high');
  const [isLocalRecording, setIsLocalRecording] = useState(false);
  const [localRecordingTime, setLocalRecordingTime] = useState(0);
  const [localRecordingPath, setLocalRecordingPath] = useState('');
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const localRecordingTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  // Socket和WebRTC相关
  const socketRef = useRef(null);
  const viewerIdRef = useRef(`viewer_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const peerConnectionRef = useRef(null);
  const pendingRemoteIceCandidatesRef = useRef([]);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const lastRequestedClassIdRef = useRef(null);
  const videoRef = useRef(null);
  const fullscreenContainerRef = useRef(null);
  const canvasRef = useRef(null); // 用于人脸检测
  const canCaptureMedia = isSuperAdmin() || membershipCaptureAllowed;
  const selectedRecordingQuality = VIDEO_RECORDING_QUALITY_OPTIONS[recordingQuality] || VIDEO_RECORDING_QUALITY_OPTIONS.high;

  // WebRTC配置 - 使用国内可访问的STUN服务器
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.miwifi.com:3478' },
      { urls: 'stun:stun.chat.bilibili.com:3478' },
      { urls: 'stun:stun.qq.com:3478' },
      { urls: 'stun:stun.cloudflare.com:3478' },
      { urls: 'stun:stun.l.google.com:19302' },
      {
        urls: 'turn:120.238.95.54:3478?transport=udp',
        username: 'jsxlb',
        credential: 'JslbTurn2026_uwlky5x8svs7nuw9'
      },
      {
        urls: 'turn:120.238.95.54:3478?transport=tcp',
        username: 'jsxlb',
        credential: 'JslbTurn2026_uwlky5x8svs7nuw9'
      }
    ],
    iceTransportPolicy: 'all',
    iceCandidatePoolSize: 6
  };

  useEffect(() => {
    loadClasses();
    checkCapturePermission();

    return () => {
      // 清理连接
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (localRecordingTimerRef.current) {
        clearInterval(localRecordingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenContainerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!viewingClass) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return undefined;
    }

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        message.warning('超过 3 分钟未操作鼠标，已自动关闭当前摄像头画面');
        stopViewing();
      }, 3 * 60 * 1000);
    };

    const mouseEvents = ['mousemove', 'mousedown', 'wheel'];
    mouseEvents.forEach((eventName) => {
      document.addEventListener(eventName, resetInactivityTimer, true);
    });
    resetInactivityTimer();

    return () => {
      mouseEvents.forEach((eventName) => {
        document.removeEventListener(eventName, resetInactivityTimer, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [viewingClass]);

  const checkCapturePermission = async () => {
    if (isSuperAdmin()) {
      setMembershipCaptureAllowed(true);
      return;
    }

    try {
      let response;
      try {
        response = await api.get('/memberships/check');
      } catch (primaryError) {
        response = await api.get('/membership/check');
      }
      setMembershipCaptureAllowed(!!(response.data?.data?.active ?? response.data?.active));
    } catch (error) {
      console.error('检查会员权限失败:', error);
      setMembershipCaptureAllowed(false);
    }
  };

  // 加载班级列表
  const loadClasses = async ({ page = currentPage, size = pageSize, keyword = searchKeyword } = {}) => {
    try {
      setLoading(true);
      const params = {
        page,
        pageSize: size,
        schoolId: user?.workbenchSchoolId
      };
      const trimmedKeyword = String(keyword || '').trim();
      if (trimmedKeyword) {
        params.search = trimmedKeyword;
      }
      const response = await api.get('/classes', { params });

      if (response.data.success) {
        const classesData = response.data.data || [];
        setFilteredClasses(classesData); // 初始化过滤列表
        setClassTotal(Number(response.data.pagination?.total ?? classesData.length));
        setCurrentPage(page);
        setPageSize(size);
        loadCameraStatus(classesData.map(item => item.id || item.classId || item.class_id).filter(Boolean));
      } else {
        message.error('加载班级列表失败');
      }
    } catch (error) {
      console.error('加载班级列表失败:', error);
      message.error('加载班级列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 搜索班级
  const handleSearch = (value) => {
    const keyword = String(value || '').trim();
    setSearchKeyword(keyword);
    loadClasses({ page: 1, size: pageSize, keyword });
  };

  // 加载摄像头在线状态
  const loadCameraStatus = async (classIds = []) => {
    try {
      if (!classIds.length) {
        setCameraStatus({});
        return;
      }
      const response = await api.get('/camera/status', {
        params: {
          classIds: classIds.join(',')
        }
      });

      if (response.data.success) {
        const statusMap = {};
        response.data.data.forEach(item => {
          const desktopOnline = item.desktopOnline ?? item.pusherOnline ?? false;
          const cameraOnline = item.cameraOnline === true;
          statusMap[item.classId] = {
            online: cameraOnline,
            desktopOnline,
            cameraOnline,
            viewerCount: item.viewerCount
          };
        });
        setCameraStatus(statusMap);
        console.log('摄像头状态:', statusMap);
      }
    } catch (error) {
      console.error('加载摄像头状态失败:', error);
    }
  };

  // 远程启动桌面客户端摄像头
  const handleStartCamera = async (classItem) => {
    console.log('🔴 [DEBUG] handleStartCamera 被调用');
    console.log('🔴 [DEBUG] classItem:', classItem);

    try {
      console.log('📷 发送启动摄像头指令:', classItem.id);
      message.loading({ content: '正在启动摄像头...', key: 'camera-start', duration: 0 });

      const response = await api.post('/camera/start-camera', {
        classId: classItem.id,
        videoQuality: selectedRecordingQuality
      });

      if (response.data.success) {
        const cameraEnabled = response.data.data?.enabled !== false;
        message.success({ content: cameraEnabled ? '摄像头已启动' : '启动指令已发送', key: 'camera-start', duration: 3 });

        setCameraStatus(prev => ({
          ...prev,
          [classItem.id]: {
            ...(prev[classItem.id] || {}),
            desktopOnline: true,
            cameraOnline: cameraEnabled,
            online: cameraEnabled
          }
        }));
      } else {
        message.error({ content: response.data.message || '启动失败', key: 'camera-start' });
      }
    } catch (error) {
      console.error('启动摄像头失败:', error);
      message.error({ content: '启动失败：' + (error.response?.data?.message || error.message), key: 'camera-start' });
    }
  };

  // 远程停止桌面客户端摄像头
  const handleStopCamera = async (classItem) => {
    try {
      console.log('📷 发送停止摄像头指令:', classItem.id);
      message.loading({ content: '正在停止摄像头...', key: 'camera-stop', duration: 0 });

      const response = await api.post('/camera/stop-camera', {
        classId: classItem.id
      });

      if (response.data.success) {
        message.success({ content: '停止指令已发送', key: 'camera-stop', duration: 2 });

        setCameraStatus(prev => ({
          ...prev,
          [classItem.id]: {
            ...(prev[classItem.id] || {}),
            cameraOnline: false,
            online: false
          }
        }));
      } else {
        message.error({ content: response.data.message || '停止失败', key: 'camera-stop' });
      }
    } catch (error) {
      console.error('停止摄像头失败:', error);
      message.error({ content: '停止失败：' + (error.response?.data?.message || error.message), key: 'camera-stop' });
    }
  };

  const loadRemoteCameraDevices = async (classId) => {
    if (!classId) return;

    try {
      setCameraDevicesLoading(true);
      const response = await api.get(`/camera/devices/${classId}`);

      if (response.data.success) {
        const devices = response.data.data?.devices || [];
        setCameraDevices(devices);
        setSelectedCameraDeviceId(response.data.data?.selectedDeviceId || devices[0]?.deviceId);
      } else {
        message.error(response.data.message || '获取摄像头列表失败');
      }
    } catch (error) {
      console.error('获取摄像头列表失败:', error);
      message.error(error.response?.data?.message || '获取摄像头列表失败');
    } finally {
      setCameraDevicesLoading(false);
    }
  };

  const handleSwitchCamera = async (deviceId) => {
    if (!viewingClass?.id) {
      message.error('请先打开班级视频');
      return;
    }

    try {
      setCameraSwitching(true);
      const response = await api.post('/camera/switch-camera', {
        classId: viewingClass.id,
        deviceId: deviceId || null,
        videoQuality: selectedRecordingQuality
      });

      if (response.data.success) {
        const devices = response.data.data?.devices || [];
        setCameraDevices(devices);
        setSelectedCameraDeviceId(response.data.data?.selectedDeviceId || deviceId);
        message.success(response.data.message || '摄像头已切换');
      } else {
        message.error(response.data.message || '切换摄像头失败');
      }
    } catch (error) {
      console.error('切换摄像头失败:', error);
      message.error(error.response?.data?.message || '切换摄像头失败');
    } finally {
      setCameraSwitching(false);
    }
  };

  // 开始查看摄像头
  const getGatewayUrl = () => {
    const configured = process.env.REACT_APP_GATEWAY_URL || process.env.REACT_APP_WS_URL;
    if (configured) return configured;

    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const url = new URL(baseUrl, window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if ((url.hostname === '127.0.0.1' || url.hostname === 'localhost') && (url.port === '4100' || url.port === '3000')) {
      url.port = '4200';
    }
    url.pathname = '/ws';
    url.search = '';
    url.hash = '';
    return url.toString();
  };

  const sendCameraSignal = (payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify({
      type: 'camera.signal',
      targetClass: viewingClass?.id || payload.classId,
      viewerId: viewerIdRef.current,
      ...payload
    }));
    return true;
  };

  const closeCameraGateway = () => {
    if (socketRef.current) {
      try {
        if (socketRef.current.readyState === WebSocket.OPEN && viewingClass?.id) {
          sendCameraSignal({
            signal: 'viewer-left',
            targetClass: viewingClass.id
          });
        }
        socketRef.current.close();
      } catch (error) {
        console.warn('close camera gateway failed', error);
      }
      socketRef.current = null;
    }
  };

  const setupGatewayHandlers = (socket, classId) => {
    socket.onopen = () => {
      socket.send(JSON.stringify({
        type: 'identity',
        role: 'teacher',
        token: localStorage.getItem('token')
      }));
    };

    socket.onmessage = async (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === 'identity:accepted') {
        sendCameraSignal({ signal: 'viewer-ready', targetClass: classId });
        await startWebRTCConnection(classId);
        return;
      }

      if (data.type === 'camera.signal') {
        if (data.signal === 'answer' && peerConnectionRef.current && data.answer) {
          const pc = peerConnectionRef.current;
          if (pc.signalingState === 'have-local-offer') {
            await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
            const pendingCandidates = pendingRemoteIceCandidatesRef.current.splice(0);
            for (const candidate of pendingCandidates) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (error) {
                console.error('add queued remote ICE candidate failed', error);
              }
            }
          }
          return;
        }

        if (data.signal === 'ice-candidate' && peerConnectionRef.current && data.candidate) {
          console.log('Remote ICE candidate:', describeIceCandidate(data.candidate));
          if (!peerConnectionRef.current.remoteDescription) {
            pendingRemoteIceCandidatesRef.current.push(data.candidate);
            return;
          }
          try {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (error) {
            console.error('add remote ICE candidate failed', error);
          }
          return;
        }

        if (data.signal === 'camera-error') {
          if (data.diagnostics) {
            console.warn('[Camera] remote peer diagnostics:', data.diagnostics);
          }
          message.error(data.message || '摄像头连接失败');
        }
        return;
      }

      if (data.type === 'camera.signal:status' && data.online === false) {
        message.warning(data.message || '教室桌面客户端离线');
        return;
      }

      if (data.type === 'error') {
        message.error(data.message || '摄像头连接失败');
      }
    };

    socket.onclose = () => {
      console.log('camera gateway closed');
    };

    socket.onerror = (error) => {
      console.error('camera gateway error', error);
      message.error('摄像头连接通道异常');
    };
  };

  const startViewing = async (classItem) => {
    try {
      console.log('开始查看摄像头:', classItem);

      // 1. 桌面客户端在线不等于摄像头已启动；查看考勤不能隐式占用摄像头。
      const status = cameraStatus[classItem.id];
      console.log('摄像头状态:', status);

      if (!status?.desktopOnline) {
        message.warning('桌面客户端离线，无法启动或查看摄像头');
        return;
      }

      if (!status?.cameraOnline) {
        message.warning('摄像头未启动，请先点击“启动摄像头”，避免误占用教室摄像头');
        return;
      }

      // 2. 开始查看
      setViewingClass(classItem);
      loadRemoteCameraDevices(classItem.id);

      closeCameraGateway();
      socketRef.current = new WebSocket(getGatewayUrl());
      setupGatewayHandlers(socketRef.current, classItem.id);

    } catch (error) {
      console.error('开始查看失败:', error);
      message.error('连接失败：' + error.message);
      setViewingClass(null);
    }
  };

  // 设置Socket事件处理
  const setupSocketHandlers = (classId) => {
    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('信令服务器连接成功');
    });

    socket.on('disconnect', () => {
      console.log('信令服务器断开连接');
      message.warning('连接已断开');
    });

    socket.on('room-joined', (data) => {
      console.log('已加入房间:', data);

      if (!data.pusherOnline) {
        message.warning('摄像头离线，等待上线...');
        return;
      }

      // 推流端在线，开始WebRTC连接
      startWebRTCConnection(classId);
    });

    socket.on('pusher-ready', () => {
      console.log('推流端就绪，开始连接');
      startWebRTCConnection(classId);
    });

    socket.on('pusher-offline', () => {
      console.log('推流端离线');
      message.warning('摄像头已离线');
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    });

    socket.on('answer', async (data) => {
      console.log('收到Answer');
      try {
        if (!peerConnectionRef.current) {
          console.error('❌ PeerConnection不存在');
          return;
        }

        // 检查当前信令状态
        const signalingState = peerConnectionRef.current.signalingState;
        console.log('📊 当前信令状态:', signalingState);

        // 只有在have-local-offer状态才能setRemoteDescription(answer)
        if (signalingState === 'have-local-offer') {
          await peerConnectionRef.current.setRemoteDescription(
            new RTCSessionDescription(data.answer)
          );
          console.log('✅ 设置远程描述成功');
        } else {
          console.warn(`⚠️ 状态不正确，忽略answer。期望: have-local-offer, 实际: ${signalingState}`);
        }
      } catch (error) {
        console.error('❌ 设置远程描述失败:', error);
      }
    });

    socket.on('ice-candidate', async (data) => {
      console.log('收到ICE candidate');
      if (peerConnectionRef.current && data.candidate) {
        try {
          await peerConnectionRef.current.addIceCandidate(
            new RTCIceCandidate(data.candidate)
          );
        } catch (error) {
          console.error('添加ICE candidate失败:', error);
        }
      }
    });

    socket.on('error', (data) => {
      console.error('服务器错误:', data);
      message.error(data.message || '发生错误');
    });
  };

  // 开始WebRTC连接
  const retryWebRTCConnection = async (classId, reason, pc) => {
    const stats = await summarizeRtcStats(pc);
    console.warn('[Camera] WebRTC direct connection failed:', {
      reason,
      attempt: reconnectAttemptRef.current,
      stats
    });

    if (classId !== lastRequestedClassIdRef.current) {
      return;
    }

    if (reconnectAttemptRef.current >= WEBRTC_MAX_RECONNECT_ATTEMPTS) {
      message.error('视频直连失败。跨网络如果 NAT 或防火墙阻断 UDP，纯 P2P 无法保证打通，需要同网、公网网络或 TURN 中继。');
      return;
    }

    reconnectAttemptRef.current += 1;
    message.warning(`视频直连失败，正在自动重连 ${reconnectAttemptRef.current}/${WEBRTC_MAX_RECONNECT_ATTEMPTS}`);

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }

    reconnectTimerRef.current = setTimeout(() => {
      if (classId === lastRequestedClassIdRef.current) {
        startWebRTCConnection(classId, { reconnect: true });
      }
    }, 1200);
  };

  const startWebRTCConnection = async (classId, options = {}) => {
    try {
      lastRequestedClassIdRef.current = classId;
      if (!options.reconnect) {
        reconnectAttemptRef.current = 0;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      console.log('开始建立WebRTC连接, classId:', classId);

      // 如果已有连接，先关闭
      if (peerConnectionRef.current) {
        console.log('⚠️ 关闭旧的PeerConnection');
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // 创建PeerConnection
      const pc = new RTCPeerConnection(rtcConfig);
      peerConnectionRef.current = pc;
      pendingRemoteIceCandidatesRef.current = [];

      if (typeof pc.addTransceiver === 'function') {
        pc.addTransceiver('video', { direction: 'recvonly' });
      }

      // 监听ICE candidate
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('Local ICE candidate:', describeIceCandidate(event.candidate));
          console.log('发送ICE candidate');
          sendCameraSignal({
            signal: 'ice-candidate',
            targetClass: classId,
            candidate: toPlainIceCandidate(event.candidate)
          });
        } else {
          console.log('[Camera] local ICE gathering complete');
        }
      };

      // 监听远程媒体流
      pc.ontrack = (event) => {
        console.log('✅ 收到远程媒体流');
        if (videoRef.current) {
          const stream = event.streams?.[0] || new MediaStream([event.track]);
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.error('播放失败:', e));
          message.success('视频连接成功！');
        }
      };

      // 监听连接状态
      pc.onconnectionstatechange = () => {
        console.log('🔗 WebRTC连接状态:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          console.log('✅ WebRTC连接成功');
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          message.error('WebRTC直连失败，请检查两台电脑是否同网段、教室电脑防火墙是否允许桌面客户端入站 UDP');
          message.error('连接失败或断开');
        }
      };

      // 创建Offer
      console.log('创建offer...');
      pc.ontrack = (event) => {
        console.log('[Camera] received remote media stream');
        if (videoRef.current) {
          const stream = event.streams?.[0] || new MediaStream([event.track]);
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(error => console.error('[Camera] video play failed:', error));
          message.success('视频连接成功');
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('[Camera] WebRTC connection state:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          reconnectAttemptRef.current = 0;
        } else if (pc.connectionState === 'failed') {
          retryWebRTCConnection(classId, 'connection-state-failed', pc);
        } else if (pc.connectionState === 'disconnected') {
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            if (pc.connectionState === 'disconnected') {
              retryWebRTCConnection(classId, 'connection-state-disconnected', pc);
            }
          }, 5000);
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('[Camera] ICE connection state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          reconnectAttemptRef.current = 0;
        } else if (pc.iceConnectionState === 'failed') {
          retryWebRTCConnection(classId, 'ice-state-failed', pc);
        } else if (pc.iceConnectionState === 'disconnected') {
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            if (pc.iceConnectionState === 'disconnected') {
              retryWebRTCConnection(classId, 'ice-state-disconnected', pc);
            }
          }, 5000);
        }
      };

      const offer = await pc.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: false
      });
      await pc.setLocalDescription(offer);
      console.log('设置本地描述成功，发送offer');

      // 发送Offer给推流端
      sendCameraSignal({
        signal: 'offer',
        targetClass: classId,
        offer: offer
      });

    } catch (error) {
      console.error('❌ 建立WebRTC连接失败:', error);
      message.error('建立连接失败：' + error.message);
    }
  };

  // 停止查看
  const stopViewing = () => {
    const classId = viewingClass?.id;
    console.log('停止查看');

    // 如果正在录像，先停止
    if (isRecording) {
      stopRecording();
    }
    if (isLocalRecording && classId) {
      api.post('/camera/stop-local-recording', { classId }).catch((error) => {
        console.warn('stop local recording failed', error);
      });
    }

    // 清理录像计时器
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (localRecordingTimerRef.current) {
      clearInterval(localRecordingTimerRef.current);
      localRecordingTimerRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    lastRequestedClassIdRef.current = null;

    closeCameraGateway();

    if (classId) {
      api.post('/camera/stop-camera', { classId }).catch((error) => {
        console.warn('stop remote camera failed', error);
      });
      setCameraStatus(prev => ({
        ...prev,
        [classId]: {
          ...(prev[classId] || {}),
          cameraOnline: false,
          online: false
        }
      }));
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setViewingClass(null);
    setIsFullscreen(false);
    setIsAttendanceMode(false);
    setAttendanceResults([]);
    setDetectedFaceCount(0);
    setIsRecording(false);
    setRecordingTime(0);
    setIsLocalRecording(false);
    setLocalRecordingTime(0);
    setCameraDevices([]);
    setSelectedCameraDeviceId(undefined);
  };

  // 统计人数（基础功能 - 不依赖座位布局）
  const countStudents = async () => {
    if (!viewingClass) {
      message.error('请先选择班级');
      return;
    }

    if (!videoRef.current || videoRef.current.videoWidth === 0) {
      message.error('视频未就绪，请稍候再试');
      return;
    }

    try {
      setAttendanceLoading(true);
      console.log('🔢 [人数统计] 开始统计人数');

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // 设置canvas尺寸
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // 绘制当前视频帧
      ctx.drawImage(video, 0, 0);

      // 获取图像数据
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // 简单的人数检测算法（基于颜色分布）
      const estimatedCount = detectPeopleCount(imageData, canvas.width, canvas.height);

      console.log('✅ [人数统计] 检测完成，估算人数:', estimatedCount);

      // 显示结果
      message.success(`检测到约 ${estimatedCount} 名学生`, 5);
      setDetectedFaceCount(estimatedCount);

    } catch (error) {
      console.error('❌ [人数统计] 统计失败:', error);
      message.error('统计失败：' + error.message);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // 检测画面中的人数（改进算法：适配教室监控画面）
  const detectPeopleCount = (imageData, width, height) => {
    const data = imageData.data;

    // 将画面分成多个区域进行检测（模拟座位）
    const gridRows = 6; // 假设6行座位
    const gridCols = 8; // 假设8列座位
    const cellWidth = width / gridCols;
    const cellHeight = height / gridRows;

    let peopleCount = 0;

    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const x = Math.floor(col * cellWidth);
        const y = Math.floor(row * cellHeight);

        // 检测该区域是否有人
        const hasPerson = detectPersonInArea(data, x, y, cellWidth, cellHeight, width);
        if (hasPerson) {
          peopleCount++;
        }
      }
    }

    return peopleCount;
  };

  // 检测指定区域是否有人
  const detectPersonInArea = (data, startX, startY, areaWidth, areaHeight, imageWidth) => {
    let brightPixels = 0;
    let darkPixels = 0;
    let midBrightPixels = 0;
    let totalSamples = 0;

    // 采样检测（每隔10个像素检测一次）
    const step = 10;
    for (let y = startY; y < startY + areaHeight && y < imageWidth * 4; y += step) {
      for (let x = startX; x < startX + areaWidth && x < imageWidth; x += step) {
        const index = (Math.floor(y) * imageWidth + Math.floor(x)) * 4;

        if (index + 2 >= data.length) continue;

        const r = data[index];
        const g = data[index + 1];
        const b = data[index + 2];

        // 计算亮度
        const brightness = (r + g + b) / 3;

        totalSamples++;

        if (brightness > 180) {
          brightPixels++;
        } else if (brightness > 100) {
          midBrightPixels++;
        } else {
          darkPixels++;
        }
      }
    }

    if (totalSamples === 0) return false;

    const brightRatio = brightPixels / totalSamples;
    const darkRatio = darkPixels / totalSamples;
    const midRatio = midBrightPixels / totalSamples;

    // 判断标准：有学生的区域应该有白色桌面、深色头发、中等亮度人体
    const hasWhiteDesk = brightRatio > 0.15;
    const hasDarkHair = darkRatio > 0.1 && darkRatio < 0.7;
    const hasPersonBody = midRatio > 0.2;

    return hasWhiteDesk && hasDarkHair && hasPersonBody;
  };

  // 开始考勤
  const startAttendance = async () => {
    if (!viewingClass) {
      message.error('请先选择班级');
      return;
    }

    try {
      setAttendanceLoading(true);
      console.log('🎯 [考勤] 开始考勤，班级ID:', viewingClass.id);

      // 获取班级学生列表
      console.log('📡 [考勤] 请求学生数据:', `/video-attendance/class-students/${viewingClass.id}`);
      const response = await api.get(`/video-attendance/class-students/${viewingClass.id}`);

      console.log('📦 [考勤] API响应:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message);
      }

      const data = response.data.data;

      // 兼容处理：data可能直接是数组（旧格式）或对象（新格式）
      let students, layout;

      if (Array.isArray(data)) {
        // 旧格式：直接是学生数组
        students = data;
        layout = null;
        console.log('⚠️ [考勤] 使用旧API格式（仅学生数组）');
      } else if (data && typeof data === 'object') {
        // 新格式：包含students和layout
        students = data.students || [];
        layout = data.layout || null;
        console.log('✅ [考勤] 使用新API格式（包含布局）');
      } else {
        students = [];
        layout = null;
      }

      console.log('👥 [考勤] 获取到学生数量:', students.length);
      console.log('👥 [考勤] 学生数据示例:', students[0]);
      console.log('📐 [考勤] 座位布局:', layout);

      // 如果没有布局配置，尝试从学生数据计算
      if (!layout && students.length > 0) {
        const studentsWithSeats = students.filter(s => s.row_number && s.col_number);
        if (studentsWithSeats.length > 0) {
          const maxRow = Math.max(...studentsWithSeats.map(s => s.row_number));
          const maxCol = Math.max(...studentsWithSeats.map(s => s.col_number));
          layout = {
            rows: maxRow,
            cols: maxCol,
            layoutMode: 'single',
            aislePositions: []
          };
          console.log('🔧 [考勤] 从学生数据计算布局:', layout);
        }
      }

      if (students.length === 0) {
        message.warning('该班级暂无学生数据，请先添加学生！');
        setIsAttendanceMode(false);
        return;
      }

      setClassStudents(students);
      setSeatLayout(layout); // 保存座位布局信息

      // 初始化考勤结果（所有学生默认缺勤）
      const initialResults = students.map(student => ({
        id: student.id,
        student_name: student.student_name,
        seat_number: student.seat_number,
        seat_row: student.row_number,
        seat_col: student.col_number,
        status: 'absent', // 默认缺勤
        face_detected: false,
        confidence: 0
      }));

      console.log('📋 [考勤] 初始化考勤结果:', initialResults.length, '条');
      console.log('📋 [考勤] 考勤结果示例:', initialResults[0]);

      console.log('📝 [考勤] 准备设置状态...');
      setAttendanceResults(initialResults);
      console.log('📝 [考勤] attendanceResults已设置');

      setIsAttendanceMode(true);
      console.log('📝 [考勤] isAttendanceMode已设置为true');

      message.info(`考勤模式已启动，检测到 ${students.length} 名学生`);

      console.log('🎬 [考勤] 准备开始人脸检测...');
      // 根据考勤方式选择不同的检测方法
      if (attendanceMethod === 'count') {
        // 方案1: 基于座位的人数统计
        startSeatBasedDetection(initialResults);
      } else if (attendanceMethod === 'face') {
        // 方案2: 百度人脸识别API
        startBaiduFaceRecognition(initialResults);
      }
      console.log('✅ [考勤] startAttendance执行完成');

    } catch (error) {
      console.error('❌ [考勤] 启动考勤失败:', error);
      message.error('启动考勤失败：' + (error.response?.data?.message || error.message));
    } finally {
      setAttendanceLoading(false);
    }
  };

  // ==================== 方案1: 基于座位的人像检测 ====================
  const startSeatBasedDetection = async (results = null) => {
    console.log('🎯 [座位检测] 开始基于座位的人像检测');

    const currentResults = results || attendanceResults;
    const layout = seatLayout;

    if (currentResults.length === 0) {
      console.warn('⚠️ [座位检测] 考勤结果为空');
      return;
    }

    if (!layout || !layout.rows || !layout.cols) {
      console.warn('⚠️ [座位检测] 座位布局信息缺失');
      message.warning('座位布局未配置，请先在班级管理中设置座位');
      return;
    }

    try {
      console.log('📍 [座位检测] 座位布局:', `${layout.rows}行 x ${layout.cols}列`);

      // 等待视频加载完成
      if (!videoRef.current || videoRef.current.videoWidth === 0) {
        console.warn('⚠️ [座位检测] 视频未就绪，2秒后重试');
        setTimeout(() => startSeatBasedDetection(currentResults), 2000);
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // 设置canvas尺寸
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // 绘制当前视频帧
      ctx.drawImage(video, 0, 0);

      console.log('📊 [座位检测] 视频尺寸:', canvas.width, 'x', canvas.height);
      console.log('📊 [座位检测] 座位布局:', `${layout.rows}行 x ${layout.cols}列`);

      // 计算每个座位的区域
      const cellWidth = canvas.width / layout.cols;
      const cellHeight = canvas.height / layout.rows;

      console.log('📐 [座位检测] 每个座位区域:', cellWidth, 'x', cellHeight);

      // 检测每个座位区域
      const detectedSeats = [];
      let totalDetected = 0;

      for (let row = 1; row <= layout.rows; row++) {
        for (let col = 1; col <= layout.cols; col++) {
          // 计算区域坐标
          const x = (col - 1) * cellWidth;
          const y = (row - 1) * cellHeight;

          // 获取区域图像数据
          const imageData = ctx.getImageData(x, y, cellWidth, cellHeight);

          // 简单的人体检测：检测区域内的亮度变化
          const hasStudent = detectPersonInRegion(imageData);

          if (hasStudent) {
            detectedSeats.push({ row, col });
            totalDetected++;
          }
        }
      }

      console.log('✅ [座位检测] 检测完成，发现', totalDetected, '个座位有人');
      console.log('📍 [座位检测] 有人的座位:', detectedSeats);

      // 更新考勤结果
      const updatedResults = currentResults.map(student => {
        const studentRow = student.seat_row || student.row_number;
        const studentCol = student.seat_col || student.col_number;

        if (!studentRow || !studentCol) {
          return student; // 没有座位信息的学生保持原状态
        }

        // 检查该学生的座位是否有人
        const isPresent = detectedSeats.some(
          seat => seat.row === studentRow && seat.col === studentCol
        );

        return {
          ...student,
          status: isPresent ? 'present' : 'absent',
          face_detected: isPresent,
          confidence: isPresent ? 75 : 0
        };
      });

      setDetectedFaceCount(totalDetected);
      setAttendanceResults(updatedResults);

      message.success(`座位检测完成：检测到 ${totalDetected} 个座位有人`);

    } catch (error) {
      console.error('❌ [座位检测] 检测失败:', error);
      message.error('座位检测失败：' + error.message);
    }
  };

  // 检测区域内是否有人（改进算法：适配教室监控画面）
  const detectPersonInRegion = (imageData) => {
    const data = imageData.data;
    const totalPixels = imageData.width * imageData.height;

    let brightPixels = 0;
    let darkPixels = 0;
    let midBrightPixels = 0;
    let bluePixels = 0; // 检测蓝色（校服、桌子）

    // 采样检测（每隔5个像素检测一次，提高准确度）
    for (let i = 0; i < data.length; i += 20) { // 每5个像素 * 4个通道
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // 计算亮度
      const brightness = (r + g + b) / 3;

      // 统计亮度分布
      if (brightness > 180) {
        brightPixels++; // 很亮（白色桌面、白纸）
      } else if (brightness > 100) {
        midBrightPixels++; // 中等亮度（人、校服）
      } else {
        darkPixels++; // 暗（头发、背景）
      }

      // 检测蓝色（校服、蓝色桌子）
      if (b > r && b > g && b > 80) {
        bluePixels++;
      }
    }

    const sampleSize = data.length / 20;
    const brightRatio = brightPixels / sampleSize;
    const darkRatio = darkPixels / sampleSize;
    const midRatio = midBrightPixels / sampleSize;
    const blueRatio = bluePixels / sampleSize;

    // 判断标准（针对教室监控画面优化）：
    // 1. 有学生的座位：有白色（桌面）、有深色（头发）、有蓝色（校服/桌子）、有中等亮度（人体）
    // 2. 空座位：主要是桌面和背景，缺少人体特征

    const hasWhiteDesk = brightRatio > 0.15; // 有白色桌面
    const hasDarkHair = darkRatio > 0.1 && darkRatio < 0.7; // 有深色但不是全黑
    const hasPersonBody = midRatio > 0.25; // 有中等亮度的人体
    const hasBlueUniform = blueRatio > 0.08; // 有蓝色校服或桌子

    // 有学生的座位应该同时满足多个条件
    const hasStudent = hasWhiteDesk && hasDarkHair && hasPersonBody;

    return hasStudent;
  };

  // ==================== 方案2: 百度人脸识别API ====================
  const startBaiduFaceRecognition = async (results = null) => {
    console.log('🤖 [百度AI] 开始人脸识别');

    const currentResults = results || attendanceResults;
    if (currentResults.length === 0) {
      console.warn('⚠️ [百度AI] 考勤结果为空');
      return;
    }

    try {
      // TODO: 实现百度人脸识别
      // 1. 获取视频帧并转为base64
      // 2. 调用百度人脸检测API
      // 3. 与学生人脸库对比
      // 4. 返回识别结果

      console.log('📸 [百度AI] 准备调用百度人脸识别API...');

      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // 设置canvas尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // 绘制当前视频帧
        ctx.drawImage(video, 0, 0);

        // 转换为base64
        const imageBase64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];

        console.log('📤 [百度AI] 图片已准备，大小:', imageBase64.length, '字节');
        console.log('💡 [百度AI] 当前为手动标记模式，请查看视频并点击"切换"按钮标记学生');

        // TODO: 调用后端API，后端再调用百度API
        // const response = await api.post('/video-attendance/baidu-face-detect', {
        //   classId: viewingClass.id,
        //   imageBase64: imageBase64
        // });

      } else {
        console.warn('⚠️ [百度AI] 视频或Canvas未就绪');
      }

    } catch (error) {
      console.error('❌ [百度AI] 识别失败:', error);
      message.error('人脸识别失败：' + error.message);
    }
  };

  // 保存考勤结果
  const saveAttendance = async () => {
    try {
      setAttendanceLoading(true);

      const attendanceRecords = attendanceResults.map(result => ({
        studentId: result.id,
        studentName: result.student_name,
        status: result.status,
        faceDetected: result.face_detected,
        confidence: result.confidence,
        seatRow: result.seat_row,
        seatCol: result.seat_col,
        remarks: result.face_detected ? '视频考勤自动识别' : ''
      }));

      const response = await api.post('/video-attendance/video-session', {
        classId: viewingClass.id,
        sessionType: 'morning', // 默认早上
        detectionMethod: 'video_count',
        attendanceRecords
      });

      if (response.data.success) {
        message.success('考勤记录已保存！');
        setIsAttendanceMode(false);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('保存考勤失败:', error);
      message.error('保存考勤失败：' + error.message);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // 手动修改学生考勤状态
  const toggleStudentStatus = (studentId) => {
    const updatedResults = attendanceResults.map(result => {
      if (result.id === studentId) {
        return {
          ...result,
          status: result.status === 'present' ? 'absent' : 'present'
        };
      }
      return result;
    });
    setAttendanceResults(updatedResults);
  };

  // 渲染座位表格式的考勤结果
  const renderSeatGrid = () => {
    if (!seatLayout || !seatLayout.rows || !seatLayout.cols) {
      return null;
    }

    const rows = [];

    for (let row = 1; row <= seatLayout.rows; row++) {
      const cols = [];

      for (let col = 1; col <= seatLayout.cols; col++) {
        // 查找这个座位的学生
        const student = attendanceResults.find(
          s => (s.seat_row || s.row_number) === row && (s.seat_col || s.col_number) === col
        );

        if (student) {
          const isPresent = student.status === 'present';
          cols.push(
            <div
              key={`${row}-${col}`}
              onClick={() => toggleStudentStatus(student.id)}
              style={{
                width: '70px',
                height: '60px',
                margin: '2px',
                padding: '4px',
                border: `2px solid ${isPresent ? '#52c41a' : '#ff4d4f'}`,
                borderRadius: '4px',
                background: isPresent ? '#f6ffed' : '#fff2f0',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '12px',
                transition: 'all 0.3s'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                {student.student_name}
              </div>
              <div style={{ fontSize: '10px', color: '#666' }}>
                {student.seat_number || `${row}-${col}`}
              </div>
              {isPresent ? (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '14px', marginTop: '2px' }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '2px' }} />
              )}
            </div>
          );
        } else {
          // 空座位
          cols.push(
            <div
              key={`${row}-${col}`}
              style={{
                width: '70px',
                height: '60px',
                margin: '2px',
                padding: '4px',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
                background: '#fafafa',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '10px',
                color: '#ccc'
              }}
            >
              空
            </div>
          );
        }
      }

      rows.push(
        <div key={row} style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{
            fontSize: '10px',
            color: '#999',
            width: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            第{row}行
          </div>
          {cols}
        </div>
      );
    }

    return <div>{rows}</div>;
  };

  // 全屏切换
  const toggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
        return;
      }

      if (container.requestFullscreen) {
        await container.requestFullscreen();
      }
    } catch (error) {
      console.error('Toggle fullscreen failed:', error);
      message.error('全屏切换失败，请重试');
    }
  };

  // 重新连接
  const reconnect = () => {
    stopViewing();
    setTimeout(() => {
      startViewing(viewingClass);
    }, 500);
  };

  // ==================== 拍照功能 ====================
  const takeSnapshot = () => {
    if (!canCaptureMedia) {
      message.warning('拍照和录像仅对会员开放');
      return;
    }

    if (!videoRef.current || videoRef.current.videoWidth === 0) {
      message.error('视频未就绪，请稍候再试');
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // 直接保存截图，不添加水印
      const now = new Date();

      // 转换为Blob并下载
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `考勤截图_${viewingClass?.name}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        message.success('截图已保存');
      }, 'image/jpeg', 0.9);

    } catch (error) {
      console.error('拍照失败:', error);
      message.error('拍照失败：' + error.message);
    }
  };

  const applyRemoteRecordingQuality = async () => {
    if (!viewingClass?.id) return;
    try {
      await api.post('/camera/set-quality', {
        classId: viewingClass.id,
        videoQuality: selectedRecordingQuality
      });
    } catch (error) {
      console.warn('更新远端录像清晰度失败:', error);
    }
  };

  // ==================== 视频录制功能 ====================
  const startRecording = async () => {
    if (!canCaptureMedia) {
      message.warning('拍照和录像仅对会员开放');
      return;
    }

    if (!videoRef.current || !videoRef.current.srcObject) {
      message.error('视频未就绪，请稍候再试');
      return;
    }

    try {
      await applyRemoteRecordingQuality();
      const stream = videoRef.current.srcObject;

      // 创建MediaRecorder
      const options = {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: selectedRecordingQuality.recorderBitrate
      };

      // 检查浏览器支持的格式
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);

        const now = new Date();
        const a = document.createElement('a');
        a.href = url;
        a.download = `考勤录像_${viewingClass?.name}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        message.success(`录像已保存（时长：${formatTime(recordingTime)}）`);
        setRecordingTime(0);
      };

      mediaRecorder.start(1000); // 每秒收集一次数据
      setIsRecording(true);

      // 开始计时
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      message.success('开始录像...');

    } catch (error) {
      console.error('开始录像失败:', error);
      message.error('开始录像失败：' + error.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // 停止计时
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const startLocalRecording = async () => {
    if (!canCaptureMedia) {
      message.warning('拍照和录像仅对会员开放');
      return;
    }
    if (!viewingClass?.id) {
      message.error('请先打开班级视频');
      return;
    }

    try {
      message.loading({ content: '正在发送桌面本地录像指令...', key: 'local-recording', duration: 0 });
      const response = await api.post('/camera/start-local-recording', {
        classId: viewingClass.id,
        deviceId: selectedCameraDeviceId || null,
        videoQuality: selectedRecordingQuality
      });

      if (!response.data.success) {
        throw new Error(response.data.message || '启动桌面本地录像失败');
      }

      const data = response.data.data || {};
      setIsLocalRecording(true);
      setLocalRecordingPath(data.filePath || '');
      setLocalRecordingTime(0);
      if (localRecordingTimerRef.current) clearInterval(localRecordingTimerRef.current);
      localRecordingTimerRef.current = setInterval(() => {
        setLocalRecordingTime(prev => prev + 1);
      }, 1000);
      message.success({ content: '桌面端已开始本地录像', key: 'local-recording', duration: 2 });
    } catch (error) {
      console.error('启动桌面本地录像失败:', error);
      message.error({ content: error.response?.data?.message || error.message || '启动桌面本地录像失败', key: 'local-recording' });
    }
  };

  const stopLocalRecording = async () => {
    if (!viewingClass?.id) return;

    try {
      message.loading({ content: '正在停止桌面本地录像...', key: 'local-recording', duration: 0 });
      const response = await api.post('/camera/stop-local-recording', {
        classId: viewingClass.id
      });

      if (!response.data.success) {
        throw new Error(response.data.message || '停止桌面本地录像失败');
      }

      const data = response.data.data || {};
      setIsLocalRecording(false);
      if (localRecordingTimerRef.current) {
        clearInterval(localRecordingTimerRef.current);
        localRecordingTimerRef.current = null;
      }
      setLocalRecordingTime(0);
      if (data.filePath) setLocalRecordingPath(data.filePath);
      message.success({
        content: data.filePath ? `桌面本地录像已保存：${data.filePath}` : '桌面本地录像已停止',
        key: 'local-recording',
        duration: 5
      });
    } catch (error) {
      console.error('停止桌面本地录像失败:', error);
      message.error({ content: error.response?.data?.message || error.message || '停止桌面本地录像失败', key: 'local-recording' });
    }
  };

  // 格式化时间显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div>
      {/* 考勤方式选择 */}
      <Card style={{ marginBottom: '16px' }}>
        <Alert
          message="选择考勤方式"
          description="按人数考勤：统计画面中的人数并与应到人数对比；AI人脸识别：识别学生人脸并自动匹配学生信息（需配置学生照片）"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Space size="large">
          <span style={{ fontWeight: 'bold' }}>考勤方式：</span>
          <Radio.Group
            value={attendanceMethod}
            onChange={(e) => setAttendanceMethod(e.target.value)}
            size="large"
          >
            <Radio.Button value="count">
              <TeamOutlined /> 按人数考勤（推荐）
            </Radio.Button>
            <Radio.Button value="face">
              <RobotOutlined /> AI人脸识别考勤
            </Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      {/* 班级摄像头列表 */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>
            <VideoCameraOutlined /> 选择班级进行考勤
          </h3>

          {/* 班级搜索框 */}
          <Search
            placeholder="搜索班级名称、年级..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 300 }}
            onSearch={handleSearch}
            onChange={(e) => {
              if (!e.target.value) {
                handleSearch('');
              }
            }}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : filteredClasses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
            {searchKeyword ? `未找到包含"${searchKeyword}"的班级` : '暂无班级数据'}
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {filteredClasses
                .map((classItem) => {
                  const status = cameraStatus[classItem.id];
                  const desktopOnline = status?.desktopOnline || false;
                  const cameraOnline = status?.cameraOnline || false;

                  console.log(`班级 ${classItem.id} (${classItem.name}):`, {
                    status,
                    desktopOnline,
                    cameraOnline,
                    cameraStatus: cameraStatus
                  });

                  return (
                    <Col key={classItem.id} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        hoverable
                        title={
                          <span>
                            <VideoCameraAddOutlined /> {classItem.name}
                          </span>
                        }
                        extra={
                          <Tag color={desktopOnline ? "green" : "default"}>
                            {desktopOnline ? "桌面在线" : "桌面离线"}
                          </Tag>
                        }
                        actions={
                          cameraOnline ? [
                            // 摄像头在线：显示查看考勤和停止按钮
                            <Button
                              key="view"
                              type="primary"
                              icon={<EyeOutlined />}
                              onClick={() => startViewing(classItem)}
                            >
                              查看考勤
                            </Button>,
                            <Button
                              key="stop"
                              type="default"
                              danger
                              icon={<CloseOutlined />}
                              onClick={() => handleStopCamera(classItem)}
                            >
                              停止摄像头
                            </Button>
                          ] : desktopOnline ? [
                            <Button
                              key="start"
                              type="primary"
                              icon={<VideoCameraOutlined />}
                              onClick={() => handleStartCamera(classItem)}
                            >
                              启动摄像头
                            </Button>
                          ] : [
                            <Button
                              key="start"
                              type="primary"
                              icon={<VideoCameraOutlined />}
                              disabled
                              onClick={() => handleStartCamera(classItem)}
                            >
                              桌面离线
                            </Button>
                          ]
                        }
                      >
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                          <VideoCameraOutlined
                            style={{
                              fontSize: '48px',
                              color: cameraOnline ? '#52c41a' : '#ccc'
                            }}
                          />
                          <div style={{ marginTop: '10px', color: cameraOnline ? '#52c41a' : '#999' }}>
                            {cameraOnline
                              ? '摄像头已启动'
                              : desktopOnline
                                ? '桌面在线，摄像头未启动'
                                : '桌面客户端离线'}
                          </div>
                          {status?.viewerCount > 0 && (
                            <div style={{ marginTop: '5px', fontSize: '12px', color: '#999' }}>
                              {status.viewerCount} 人正在观看
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
            </Row>

            {/* 分页组件 */}
            {classTotal > 0 && (
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={classTotal}
                  onChange={(page, size) => {
                    loadClasses({ page, size, keyword: searchKeyword });
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total) => `共 ${total} 个班级${searchKeyword ? `（搜索："${searchKeyword}"）` : ''}`}
                  pageSizeOptions={['12', '24', '48', '96']}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* 视频查看模态框 */}
      <Modal
        title={
          <span>
            <VideoCameraOutlined /> {viewingClass?.name} - 视频考勤
          </span>
        }
        open={!!viewingClass}
        onCancel={stopViewing}
        footer={[
          <Button
            key="attendance"
            type="primary"
            icon={<UserOutlined />}
            onClick={startAttendance}
            loading={attendanceLoading}
            disabled={isAttendanceMode}
          >
            {isAttendanceMode ? '考勤进行中...' : '开始考勤'}
          </Button>,
          <Button
            key="count"
            type="dashed"
            icon={<TeamOutlined />}
            onClick={countStudents}
            loading={attendanceLoading}
            disabled={isAttendanceMode}
          >
            统计人数
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={saveAttendance}
            loading={attendanceLoading}
            disabled={!isAttendanceMode}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            保存考勤
          </Button>,
          <Button key="reconnect" icon={<ReloadOutlined />} onClick={reconnect}>
            重新连接
          </Button>,
          <Button
            key="fullscreen"
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? '退出全屏' : '全屏'}
          </Button>,
          <Button key="close" icon={<CloseOutlined />} onClick={stopViewing}>
            关闭
          </Button>
        ]}
        width={isAttendanceMode ? 1400 : 1000}
        centered
      >
        <Row gutter={16}>
          <Col span={isAttendanceMode ? 12 : 24}>
            <div
              ref={fullscreenContainerRef}
              onDoubleClick={toggleFullscreen}
              style={{
                background: '#000',
                borderRadius: isFullscreen ? 0 : '8px',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                height: isFullscreen ? '100vh' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  height: isFullscreen ? '100%' : 'auto',
                  maxHeight: isFullscreen ? '100vh' : '70vh',
                  display: 'block',
                  objectFit: 'contain',
                  background: '#000'
                }}
              />
              {/* 隐藏的canvas，用于捕获视频帧进行人脸检测 */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div style={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                padding: '5px 10px',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                <span style={{ color: '#f00', marginRight: '5px' }}>●</span>
                实时直播
              </div>
              {isAttendanceMode && (
                <div style={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  background: 'rgba(0,0,0,0.8)',
                  color: '#fff',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  <RobotOutlined style={{ marginRight: '8px' }} />
                  {attendanceMethod === 'count' ? '座位检测模式' : 'AI人脸识别模式'}
                  {detectedFaceCount > 0 && ` - 检测到 ${detectedFaceCount} 人`}
                </div>
              )}
            </div>

            <Card
              size="small"
              style={{ marginTop: '16px', borderColor: '#d9f7be', background: '#f6ffed' }}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="若当前画面调用了错误摄像头，可在这里切换到白板摄像头或视频展台。"
                />
                <Space wrap>
                  <Select
                    value={selectedCameraDeviceId}
                    placeholder={cameraDevicesLoading ? '正在读取摄像头...' : '请选择摄像头'}
                    style={{ minWidth: 320 }}
                    loading={cameraDevicesLoading}
                    onChange={value => setSelectedCameraDeviceId(value)}
                    onDropdownVisibleChange={(open) => {
                      if (open) loadRemoteCameraDevices(viewingClass?.id);
                    }}
                  >
                    {cameraDevices.map((device, index) => (
                      <Option key={device.deviceId} value={device.deviceId}>
                        {device.label || `摄像头 ${index + 1}`}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => loadRemoteCameraDevices(viewingClass?.id)}
                    loading={cameraDevicesLoading}
                  >
                    刷新设备
                  </Button>
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    onClick={() => handleSwitchCamera(selectedCameraDeviceId)}
                    loading={cameraSwitching}
                    disabled={!selectedCameraDeviceId}
                  >
                    切换摄像头
                  </Button>
                </Space>
              </Space>
            </Card>

            {/* 拍照和录像按钮 */}
            {canCaptureMedia ? (
              <Space direction="vertical" size={10} style={{ marginTop: '16px', width: '100%', alignItems: 'center' }}>
                <Space wrap style={{ justifyContent: 'center' }}>
                  <span>录像清晰度</span>
                  <Select
                    value={recordingQuality}
                    style={{ width: 120 }}
                    onChange={setRecordingQuality}
                    disabled={isRecording || isLocalRecording}
                  >
                    {Object.values(VIDEO_RECORDING_QUALITY_OPTIONS).map(option => (
                      <Option key={option.key} value={option.key}>{option.label}</Option>
                    ))}
                  </Select>
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    onClick={takeSnapshot}
                    size="large"
                  >
                    拍照
                  </Button>
                </Space>
                <Space wrap style={{ justifyContent: 'center' }}>
                  {!isRecording ? (
                    <Button
                      type="primary"
                      danger
                      icon={<VideoCameraTwoTone twoToneColor="#ff4d4f" />}
                      onClick={startRecording}
                      size="large"
                    >
                      网页录像
                    </Button>
                  ) : (
                    <Button
                      type="default"
                      icon={<PauseCircleOutlined />}
                      onClick={stopRecording}
                      size="large"
                      style={{
                        background: '#fff2f0',
                        borderColor: '#ff4d4f',
                        color: '#ff4d4f'
                      }}
                    >
                      停止网页录像 ({formatTime(recordingTime)})
                    </Button>
                  )}
                  {!isLocalRecording ? (
                  <Button
                    type="primary"
                    icon={<VideoCameraTwoTone twoToneColor="#ff4d4f" />}
                    onClick={startLocalRecording}
                    size="large"
                  >
                    桌面本地录像
                  </Button>
                ) : (
                  <Button
                    type="default"
                    icon={<PauseCircleOutlined />}
                    onClick={stopLocalRecording}
                    size="large"
                    style={{
                      background: '#fff2f0',
                      borderColor: '#ff4d4f',
                      color: '#ff4d4f'
                    }}
                  >
                    停止桌面本地录像 ({formatTime(localRecordingTime)})
                  </Button>
                )}
                </Space>
                {localRecordingPath && (
                  <div style={{ color: '#666', fontSize: 12, wordBreak: 'break-all', textAlign: 'center' }}>
                    桌面端保存路径：{localRecordingPath}
                  </div>
                )}
              </Space>
            ) : (
              <Alert
                style={{ marginTop: '16px' }}
                type="warning"
                showIcon
                message="拍照和录像仅对会员开放"
              />
            )}

            <div style={{ marginTop: '16px', color: '#999', textAlign: 'center' }}>
              <p>提示：视频通过WebRTC P2P直连，不经过服务器，延迟极低</p>
            </div>
          </Col>

          {isAttendanceMode && (
            <Col span={12}>
              <div style={{
                maxHeight: '70vh',
                overflowY: 'auto',
                background: '#f5f5f5',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <h3 style={{ marginBottom: '16px' }}>
                  考勤结果
                  <Badge
                    count={attendanceResults.filter(r => r.status === 'present').length}
                    showZero
                    style={{ marginLeft: '10px', background: '#52c41a' }}
                  /> / {attendanceResults.length}
                </h3>
                {console.log('🔍 [渲染] attendanceResults数据:', attendanceResults)}
                {console.log('🔍 [渲染] attendanceResults长度:', attendanceResults.length)}
                {console.log('🔍 [渲染] isAttendanceMode:', isAttendanceMode)}
                {console.log('🔍 [渲染] seatLayout:', seatLayout)}

                {/* 如果有座位布局，显示座位图；否则显示列表 */}
                {seatLayout && seatLayout.rows && seatLayout.cols ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      讲台方向 ↑
                    </div>
                    {renderSeatGrid()}
                  </div>
                ) : (
                  <Table
                    dataSource={attendanceResults}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    locale={{ emptyText: `暂无数据 (数组长度: ${attendanceResults.length})` }}
                    columns={[
                      {
                        title: '姓名',
                        dataIndex: 'student_name',
                        key: 'student_name',
                        width: 80
                      },
                      {
                        title: '座位',
                        dataIndex: 'seat_number',
                        key: 'seat_number',
                        width: 60
                      },
                      {
                        title: '状态',
                        dataIndex: 'status',
                        key: 'status',
                        width: 80,
                        render: (status, record) => (
                          <Tag
                            color={status === 'present' ? 'green' : 'red'}
                            icon={status === 'present' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          >
                            {status === 'present' ? '出勤' : '缺勤'}
                          </Tag>
                        )
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 100,
                        render: (_, record) => (
                          <Button
                            size="small"
                            onClick={() => toggleStudentStatus(record.id)}
                          >
                            切换
                          </Button>
                        )
                      }
                    ]}
                  />
                )}
              </div>
            </Col>
          )}
        </Row>
      </Modal>

    </div>
  );
};

export default VideoAttendance;
